import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useDebug } from '../contexts/DebugContext';
// Calendar-date helpers: no browser time-zone logic (see utils/dates.js).
import { toDateOnly, displayRange } from '../utils/dates';
import { timesheetPath } from '../nav/paths';
import { IconButton } from '../components/ui';

// Time > Timesheets: the user's timesheet list (25 per page).
export default function TimesheetsPage() {
  const navigate = useNavigate();
  const { debugMode } = useDebug();
  const [timesheets, setTimesheets] = useState([]);
  const [timesheetsTotal, setTimesheetsTotal] = useState(0);
  const [timesheetsPage, setTimesheetsPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showPeriodPicker, setShowPeriodPicker] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [creating, setCreating] = useState(false);
  const [navDebugInfo, setNavDebugInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.getTimesheets(1)
      .then((data) => {
        if (cancelled) return;
        setTimesheets(data.timesheets || []);
        setTimesheetsTotal(data.total || 0);
        setTimesheetsPage(data.page || 1);
        if (data._navDebug) setNavDebugInfo(data._navDebug);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleNewTimesheet = async () => {
    try {
      // No date sent: the server picks the current period from its own "today"
      // (Central time), never the browser's clock.
      const ts = await api.getOrCreateTimesheet();
      navigate(timesheetPath(ts.TimesheetID));
    } catch (e) {
      alert(e.message || 'Failed to create timesheet. Ensure time periods have been synced.');
    }
  };

  const handleShowPeriodPicker = async () => {
    setShowPeriodPicker(true);
    setLoadingPeriods(true);
    try {
      const p = await api.getTimesheetPeriods();
      setPeriods(p);
    } catch (e) {
      alert(e.message || 'Failed to load periods.');
      setShowPeriodPicker(false);
    } finally {
      setLoadingPeriods(false);
    }
  };

  const handleCreateForPeriod = async (period) => {
    if (period.HasTimesheet && period.TimesheetID) {
      navigate(timesheetPath(period.TimesheetID));
      return;
    }
    setCreating(true);
    try {
      const ts = await api.getOrCreateTimesheet(toDateOnly(period.PeriodStartDate));
      navigate(timesheetPath(ts.TimesheetID));
    } catch (e) {
      alert(e.message || 'Failed to create timesheet for this period.');
    } finally {
      setCreating(false);
    }
  };

  // Fetch a page of timesheets (25/page) and apply it. Used by the pager and by
  // handleRefresh; init loads page 1 inline (with its cancelled guard).
  const fetchTimesheetPage = async (page) => {
    const data = await api.getTimesheets(page);
    setTimesheets(data.timesheets || []);
    setTimesheetsTotal(data.total || 0);
    setTimesheetsPage(data.page || page);
    if (data._navDebug) setNavDebugInfo(data._navDebug);
  };

  if (loading) {
    return <div className="loading"><div className="spinner" />Loading timesheets...</div>;
  }

  return (
    <>
      <div style={{ padding: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button
          className="btn btn-primary"
          style={{ flex: 1, minWidth: '150px' }}
          onClick={handleNewTimesheet}
        >
          + Current Period
        </button>
        <button
          className="btn btn-outline"
          style={{ flex: 1, minWidth: '150px' }}
          onClick={handleShowPeriodPicker}
        >
          + Prior Period
        </button>
      </div>

      {/* Period Picker Modal */}
      {showPeriodPicker && (
        <div className="modal-overlay" onClick={() => setShowPeriodPicker(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <strong>Select Period</strong>
              <IconButton icon="close" title="Close" onClick={() => setShowPeriodPicker(false)} />
            </div>
            {loadingPeriods ? (
              <div className="loading"><div className="spinner" />Loading periods...</div>
            ) : periods.length === 0 ? (
              <div className="empty-state"><p>No periods available. Sync jobs first.</p></div>
            ) : (
              <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                {periods.map((p) => {
                  const periodLabel = displayRange(p.PeriodStartDate, p.PeriodEndDate);
                  return (
                    <div
                      key={p.PeriodNo}
                      className="ts-list-item"
                      style={{ cursor: creating ? 'wait' : 'pointer' }}
                      onClick={() => !creating && handleCreateForPeriod(p)}
                    >
                      <div>
                        <div className="ts-period">{periodLabel}</div>
                        <div style={{ fontSize: '12px', color: 'var(--gray-400)' }}>Period {p.PeriodNo}</div>
                      </div>
                      {p.HasTimesheet ? (
                        <span className="ts-status pending">Open</span>
                      ) : (
                        <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>+ Create</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}



      <div className="card">
        <div className="card-header">
          <span className="card-title">My Timesheets</span>
        </div>
        {timesheets.length === 0 ? (
          <div className="empty-state">
            <p>No timesheets yet. Create one to get started.</p>
          </div>
        ) : (
          <>
            {timesheets.map((ts) => {
              const tsDebug = navDebugInfo?.find((d) => d.timesheetId === ts.TimesheetID);
              return (
                <div key={ts.TimesheetID}>
                  <div
                    className="ts-list-item"
                    onClick={() => navigate(timesheetPath(ts.TimesheetID))}
                  >
                    <div>
                      <div className="ts-period">{displayRange(ts.PeriodStart, ts.PeriodEnd)}</div>
                      {debugMode && tsDebug && (
                        <div style={{ fontSize: '10px', color: 'var(--gray-500)', fontFamily: 'monospace', marginTop: '2px' }}>
                          NAV raw="{tsDebug.debug?.rawStatus}" type={tsDebug.debug?.rawStatusType} → category="{tsDebug.debug?.category}" | DB before="{tsDebug.dbStatusBefore}" → final="{tsDebug.finalStatus}"
                        </div>
                      )}
                    </div>
                    <span className={`ts-status ${ts.Status.toLowerCase()}`}>
                      {ts.Status}
                    </span>
                  </div>
                </div>
              );
            })}

            {/* Pagination */}
            {timesheetsTotal > 25 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px' }}>
                <IconButton icon="prev" title="Previous page"
                  disabled={timesheetsPage <= 1}
                  onClick={() => fetchTimesheetPage(timesheetsPage - 1)} />
                <span style={{ fontSize: '13px', padding: '6px' }}>
                  Page {timesheetsPage} of {Math.ceil(timesheetsTotal / 25)}
                </span>
                <IconButton icon="next" title="Next page"
                  disabled={timesheetsPage >= Math.ceil(timesheetsTotal / 25)}
                  onClick={() => fetchTimesheetPage(timesheetsPage + 1)} />
              </div>
            )}
          </>
        )}
      </div>

      {/* NAV Status Debug Panel */}
      {debugMode && navDebugInfo && (
        <div style={{
          margin: '8px 16px', background: 'var(--gray-50)', border: '1px solid var(--gray-200)',
          borderRadius: 'var(--radius)', padding: '8px',
          fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all', maxHeight: '400px', overflow: 'auto',
        }}>
          <div style={{ fontWeight: 600, marginBottom: '6px' }}>NAV Status Debug (Timesheets List)</div>
          {navDebugInfo.map((d, i) => (
            <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid var(--gray-200)' }}>
              <div><strong>ID: {d.timesheetId}</strong></div>
              <div>DB Status Before: "{d.dbStatusBefore}"</div>
              <div>NAV raw status: "{d.debug?.rawStatus}" (type: {d.debug?.rawStatusType})</div>
              <div>NAV lowercase: "{d.debug?.navStatus}"</div>
              <div>Category: <strong>{d.debug?.category || d.category || 'N/A'}</strong> (matched in: {d.debug?.matchedIn || 'NONE'})</div>
              <div>Final Status: <strong>"{d.finalStatus}"</strong> (changed: {String(d.dbStatusBefore !== d.finalStatus)})</div>
              {d.error && <div style={{ color: '#dc2626' }}>Error: {d.error}</div>}
              {d.debug?.allFieldNames && <div>NAV record fields: [{d.debug.allFieldNames.join(', ')}]</div>}
              {d.debug?.response && <div style={{ marginTop: '2px', color: 'var(--gray-600)' }}>NAV Response: {JSON.stringify(d.debug.response)}</div>}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
